export type ExpiryUrgency = 'plenty' | 'soon' | 'urgent';

export function formatCompactDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) {
    return '0m';
  }

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
}

export function expiryUrgencyFromPercent(percentRemaining: number | null): ExpiryUrgency {
  if (percentRemaining == null || percentRemaining >= 75) {
    return 'plenty';
  }
  if (percentRemaining >= 25) {
    return 'soon';
  }
  return 'urgent';
}

export function expiryUrgencyLabel(urgency: ExpiryUrgency): string {
  if (urgency === 'plenty') {
    return 'Plenty of time';
  }
  if (urgency === 'soon') {
    return 'Action soon';
  }
  return 'Almost out of time';
}

export function isAutoDeclineExpired(
  autoDeclineAt: string | Date | null | undefined,
  nowMs: number,
): boolean {
  const end = toEpochMs(autoDeclineAt);
  return end != null && end <= nowMs;
}

export function toEpochMs(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    return Number.isFinite(ms) ? ms : null;
  }

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  if (typeof value === 'object') {
    const record = value as {
      toDate?: () => Date;
      seconds?: number;
      _seconds?: number;
    };
    if (typeof record.toDate === 'function') {
      return toEpochMs(record.toDate());
    }
    const seconds = record.seconds ?? record._seconds;
    if (typeof seconds === 'number') {
      return toEpochMs(seconds * 1000);
    }
  }

  const ms = new Date(value as string).getTime();
  return Number.isFinite(ms) ? ms : null;
}
